import { FieldValues } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import useInitiateBooking from "repoV2/features/Payments/utils/useInitiateBooking";
import useSetIsPaymentProcessing from "repoV2/features/CheckoutPages/utils/useSetIsPaymentProcessing";
import {
    FORM_KEYS,
    multiple_users_helpers_keys,
} from "repoV2/features/Common/modules/FormData/FormData.constants";
import { checkIsObjectEmpty } from "repoV2/utils/common/dataTypes/object";
import { booking_initial_user_id } from "repoV2/features/CheckoutPages/constants/CheckoutPages.constants";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import {
    getCountryDataBasedOnTimezone,
    getUserTimezone,
} from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { INDIA_COUNTRY_DATA } from "repoV2/utils/common/location/countriesData";
import {
    DEFAULT_PAYMENT_CARD_TYPE,
    DEFAULT_PAYMENT_METHOD,
    PAYMENT_CALCULATION_TYPE,
} from "repoV2/features/Payments/Payments.constants";
import { IBookingQuestion } from "repoV2/features/CheckoutPages/utils/apis/CustomQuestionsApi/ICustomQuestionsApi.interface";
import useGetIsPaymentProcessing from "repoV2/features/CheckoutPages/utils/useGetIsPaymentProcessing";
import {
    CUSTOM_QUESTION_TYPES_ID,
    other_dropdown_option,
} from "repoV2/features/CustomQuestions/constants/CustomQuestionsTypes.constants";
import { getOtherOptionFieldNameWithSuffix } from "repoV2/features/CustomQuestions/utils/CustomQuestions.utils";
import { IUserDetails } from "repoV2/features/Common/modules/FormData/interfaces/UserDetailsFormGroup.interfaces";
import { ISelectedSessionDetails } from "repoV2/features/Common/modules/FormData/interfaces/SessionDetailsFormGroup.interfaces";
import { ITimezoneDetails } from "repoV2/features/Common/modules/FormData/interfaces/TimezoneSelector.interfaces";
import { IInternationalDetails } from "repoV2/features/Common/modules/FormData/interfaces/InternationalDetailsFormGroup.interfaces";
import { IFormDataState } from "repoV2/features/Common/modules/FormData/interfaces/FormData";
import { checkCustomQuestionAnswerValid } from "repoV2/features/CustomQuestions/utils/checkCustomQuestionAnswerValid";
import { IGSTDetails } from "repoV2/features/Common/modules/FormData/interfaces/GSTDetailsFormGroup.interfaces";
import { IPaymentDetails } from "repoV2/features/Common/modules/FormData/interfaces/PaymentDetails.interfaces";
import { IDeliveryDetails } from "repoV2/features/Common/modules/FormData/interfaces/IDeliveryDetails.interface";
import { IMultipleUsersHelpers } from "repoV2/features/Common/modules/FormData/interfaces/MultipleUserHelpers.interfaces";
import { getShowAddressFields } from "repoV2/features/CheckoutPages/utils/getShowAddressFields";
import {
    AVAILABILITY_CHOICE,
    BOOKING_PAYMENT_TYPE,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import { checkIsListingClassesType } from "repoV2/features/CheckoutPages/utils/checkIsListingClassesType";
import { getLoggedInUserDetails } from "repoV2/utils/user&auth&login/loggedInUserDetails";
import { CHECKOUT_PAGES_ACTION_TYPES } from "repoV2/features/CheckoutPages/constants/CheckoutPagesReducer";
import { IListingSessionDetails } from "repoV2/features/CheckoutPages/utils/apis/SessionDetailsApi/SessionDetailsApi.interface";
import {
    checkout_form,
    checkout_form_error_strings,
    multiple_user_count_key,
    session_selector_enum,
} from "./CheckoutForm.constants";
import { useCheckoutBasicDetailsContext } from "../../../CheckoutBasicDetailsContextProvider";
import { getUserFormFieldName } from "../FormGroups/modules/UserDetailsFormGroup/UserDetailsFormGroup.utils";
import { ICheckoutFormDefaultValues } from "./CheckoutForm.interfaces";
import { useGetPriceSummary } from "../../../PaymentSummarySection/utils/priceSummaryReducer";
import {
    IOrderBumpsDetails,
    IOrderBumpsFormValues,
} from "../FormGroups/modules/OrderBumpsFormGroup/OrderBumpsFormGroup.interfaces";
import { order_bumps_form_key } from "../FormGroups/modules/OrderBumpsFormGroup/OrderBumpsFormGroup.constants";
import { delivery_details_form_constant } from "../FormGroups/modules/DeliveryDetailsFormGroup/DeliveryDetailsFormGroup.contants";
import { selected_session_details_form_key } from "../FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.constants";
import {
    merchandise_shiprocket_error,
    selected_merchandise_default_value,
    selected_merchandise_quantity_form_key,
    selected_merchandise_variant_form_key,
} from "../FormGroups/modules/MerchandiseDetailsFormGroup/MerchandiseDetailsFormGroup.constants";
import { checkMerchandiseShipping } from "../FormGroups/modules/MerchandiseDetailsFormGroup/utils/checkMerchandiseShipping";

const {
    user_details: {
        field_names: { name, email, phone, country_code, billing_state },
    },
    gst_details: {
        field_names: {
            active,
            company_name,
            registration_number,
            company_address,
            company_pincode,
        },
    },
    international_details: {
        field_names: {
            street_address,
            user_state,
            town_city,
            postal_code,
            payment_method,
        },
    },
    timezone_details: {
        field_names: { timezone_data, timezone_data_description_section },
    },
    terms_and_conditions_details: {
        field_names: { agree_to_terms_and_conditions },
    },
} = checkout_form;

const {
    basic_details: {
        form_fields: {
            delivery_address,
            delivery_state,
            delivery_pin_code,
            delivery_city,
        },
    },
} = delivery_details_form_constant;

/**
 * @dev
 * This function calculates the default values
 * for the checkoutForm fields from redux formData
 * This is used for the case if the checkoutForm data is filled in mobile Modal
 * and then if modal is closed and reopened then need to maintain the default state
 * of form on mount of mobile Modal
 */
const computeDefaultValuesFromRedux = (data: {
    userDetails: { [userId: string]: IUserDetails };
    sessionDetails: IListingSessionDetails;
    gstDetails: IGSTDetails;
    timezoneDetails: ITimezoneDetails;
    paymentMethod: string;
    listingUuid: string;
    listingType: number;
    communityUuid: string;
    eInvoiceEnabled?: boolean;
}) => {
    const defaultValues: ICheckoutFormDefaultValues = {};
    const {
        userDetails,
        gstDetails,
        sessionDetails,
        timezoneDetails,
        paymentMethod,
        listingUuid,
        listingType,
        communityUuid,
        eInvoiceEnabled,
    } = data;
    const timezone = getUserTimezone();
    const { dial_code: defaultDialCode = INDIA_COUNTRY_DATA.dial_code } =
        getCountryDataBasedOnTimezone({ timezone }) || {};

    // If the user is logged in, then we'll be having the user details in localstorage,
    // now let say user clicks a community bonus listing, now we need to show the booking form
    // as pre-filled to the user with their logged in details
    const loggedInUserDetails = communityUuid ? getLoggedInUserDetails() : {};

    // @dev
    // This case is handled when there is no user added in redux
    // we have to add an empty user by default to add default values to the user details form
    if (checkIsObjectEmpty(userDetails)) {
        userDetails[booking_initial_user_id] = {};
    }

    // TODO: once we actully support multiple users, we need to verify
    // if the `Object.entries(userDetails).length` is same as what the
    // end user has actually entered whil booking.
    //
    // Ideally, it should never happen. But will need to handle that scenario.
    Object.entries(userDetails).forEach(([userId, _detailsOfUserId]) => {
        // Basic user details
        // We pre-fill the default values if we have some already fetched or
        // we have to fill the details of logged in user (if it exists)
        // or we use empty string
        const detailsOfUserId = _detailsOfUserId || {};
        defaultValues[getUserFormFieldName(userId, name)] =
            detailsOfUserId.name || loggedInUserDetails.fullName || "";
        defaultValues[getUserFormFieldName(userId, email)] =
            detailsOfUserId.email || loggedInUserDetails.email || "";
        defaultValues[getUserFormFieldName(userId, phone)] =
            detailsOfUserId.phone || loggedInUserDetails.phone || "";
        defaultValues[getUserFormFieldName(userId, country_code)] =
            detailsOfUserId.country_code ||
            loggedInUserDetails.countryCode ||
            defaultDialCode;
        defaultValues[getUserFormFieldName(userId, billing_state)] =
            detailsOfUserId.billing_state || "";

        // International user details
        const internationalDetails =
            detailsOfUserId[FORM_KEYS.international_details] || {};
        defaultValues[getUserFormFieldName(userId, street_address)] =
            internationalDetails.street_address || "";
        defaultValues[getUserFormFieldName(userId, town_city)] =
            internationalDetails.town_city || "";
        defaultValues[getUserFormFieldName(userId, user_state)] =
            internationalDetails.user_state || "";
        defaultValues[getUserFormFieldName(userId, postal_code)] =
            internationalDetails.postal_code || "";
    });

    defaultValues[payment_method] = paymentMethod || DEFAULT_PAYMENT_METHOD;

    // GST details
    // Todo: Risky to keep variable names like 'active',
    // Rename to something more specific
    defaultValues[active] = Boolean(gstDetails?.active);
    defaultValues[company_name] = gstDetails?.company_name || "";
    defaultValues[registration_number] = gstDetails?.registration_number || "";

    // pass company address and pincode only if eInvoiceEnabled is true
    if (eInvoiceEnabled) {
        defaultValues[company_address] = gstDetails?.company_address || "";
        defaultValues[company_pincode] = gstDetails?.company_pincode || "";
    }

    defaultValues[delivery_address.field_name] = "";
    defaultValues[delivery_pin_code.field_name] = "";
    defaultValues[delivery_state.field_name] = "";
    defaultValues[delivery_city.field_name] = "";

    defaultValues[order_bumps_form_key] = {};

    // Schedule details
    const slots = sessionDetails?.slots || [];
    const batches = sessionDetails?.classes || [];

    let selectedSessions: string[] = [];
    let selectedBatch = {};

    if (listingType === LISTING_TYPES.WEBINAR && slots.length === 1) {
        selectedSessions = [slots[0].uuid];
    }
    if (listingType === LISTING_TYPES.WORKSHOPS) {
        selectedSessions = slots
            .filter(slot => !slot?.isPast)
            .map(slot => slot.uuid);
    }

    const isListingTypeClass =
        checkIsListingClassesType({ listingType }) && batches.length === 1;

    // In case the listing is of classes type, and there's only
    // one batch in the class, we set the value in react hook form
    if (isListingTypeClass) {
        // we only allow to select one batch in classes, and this is
        // the case where only one batch exist, so we pre select it
        selectedBatch = { value: batches[0].uuid, label: "" };
    }

    const selectedSessionKey = session_selector_enum[listingType];
    defaultValues[listingUuid] = {
        [selected_session_details_form_key]: {
            [selectedSessionKey]: isListingTypeClass
                ? selectedBatch
                : selectedSessions,
        },
    };

    // Timezone data
    defaultValues[timezone_data] = timezoneDetails?.name || timezone;
    defaultValues[timezone_data_description_section] =
        timezoneDetails?.name || timezone;

    defaultValues[multiple_user_count_key] = 1;

    defaultValues[selected_merchandise_variant_form_key] = {};
    defaultValues[selected_merchandise_quantity_form_key] =
        selected_merchandise_default_value;

    defaultValues[agree_to_terms_and_conditions] = false;

    return defaultValues;
};

/**
 * @dev
 * This function converts the checkoutForm field values
 * to the initiate booking payload
 * form values has to be used for initiate booking because if the
 * data is autofilled in mobile then some data is not saved to redux
 * (where redux update is called on field blur)
 */
const convertFormValuesToInitiateBookingPayload = (data: {
    values: FieldValues;
    userIds: string[];
    bookingQuestions: IBookingQuestion[] | undefined;
    listingUuid: string;
    listingType: number;
    showInternationalInputFields: boolean;
}) => {
    const {
        values,
        userIds,
        bookingQuestions,
        listingUuid,
        listingType,
        showInternationalInputFields,
    } = data;
    const usersDetails: { [userId: string]: IUserDetails } = {};
    const sessionDetails: ISelectedSessionDetails = {};
    const orderBumpsDetails: IOrderBumpsDetails = {};
    const deliveryDetails: IDeliveryDetails = {};
    // @dev
    // Get Multi User Details here from form values
    userIds.forEach(userId => {
        usersDetails[userId] = {};

        // userDetails - start
        const tempUserDetails = usersDetails[userId];
        tempUserDetails.name = values[getUserFormFieldName(userId, name)];
        tempUserDetails.email = values[getUserFormFieldName(userId, email)];
        tempUserDetails.phone = values[getUserFormFieldName(userId, phone)];
        tempUserDetails.country_code =
            values[getUserFormFieldName(userId, country_code)];
        tempUserDetails.billing_state =
            values[getUserFormFieldName(userId, billing_state)];
        // userDetails - end

        // internationalDetails - start
        const tempInternationalDetails: IInternationalDetails = {};
        tempInternationalDetails.street_address =
            values[getUserFormFieldName(userId, street_address)];
        // user state is either a dropdown (indian states in case of domestic users)
        // or it is a string input (any value for case of international customers)
        tempInternationalDetails.user_state = !showInternationalInputFields
            ? values[getUserFormFieldName(userId, user_state)]?.value
            : values[getUserFormFieldName(userId, user_state)];
        tempInternationalDetails.town_city =
            values[getUserFormFieldName(userId, town_city)];
        tempInternationalDetails.postal_code =
            values[getUserFormFieldName(userId, postal_code)];

        // For non international users, in the merchandise case, we display the user state as part of the address details
        // so we do not show billing state and send the same value of user state in billing state
        if (
            !showInternationalInputFields &&
            listingType === LISTING_TYPES.MERCHANDISE
        ) {
            tempUserDetails.billing_state =
                values[getUserFormFieldName(userId, user_state)];
        }

        tempUserDetails[FORM_KEYS.international_details] =
            tempInternationalDetails;
        // internationalDetails - end

        // custom booking questions - starts
        const tempBookingCustomQuestionAnswers: IUserDetails["bookingCustomQuestionAnswers"] =
            [];

        if (bookingQuestions?.length) {
            bookingQuestions.forEach(question => {
                let answer =
                    values[getUserFormFieldName(userId, question.uuid)] || "";
                // Incase answer does not exist, we do not sent it in
                // tempBookingCustomQuestionAsnwers and in api as well
                if (!checkCustomQuestionAnswerValid(question.type, answer)) {
                    return;
                }
                if (
                    question.type === CUSTOM_QUESTION_TYPES_ID.CALENDAR &&
                    answer
                ) {
                    answer = new Date(answer).toISOString();
                }
                // Incase of dropdown fields, if user selects other, we append "--other"
                // to get the actual value of the input
                if (
                    question.type === CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY ||
                    question.type === CUSTOM_QUESTION_TYPES_ID.GENDER
                ) {
                    answer =
                        answer === other_dropdown_option.value
                            ? values[
                                  getOtherOptionFieldNameWithSuffix(
                                      getUserFormFieldName(
                                          userId,
                                          question.uuid
                                      )
                                  )
                              ]
                            : answer;
                }
                tempBookingCustomQuestionAnswers.push({
                    ques: question.uuid,
                    ans: answer,
                    thumbnail:
                        values[
                            `${getUserFormFieldName(
                                userId,
                                question.uuid
                            )}-file`
                        ]?.thumbnail,
                });
            });
        }
        // custom booking questions - end

        tempUserDetails.bookingCustomQuestionAnswers =
            tempBookingCustomQuestionAnswers;
    });

    const merchandiseQuantity =
        +values?.[selected_merchandise_quantity_form_key]?.value || 1;

    // @dev
    // Get session Details here from form values
    const selectedSessionKey = session_selector_enum[listingType];

    sessionDetails[selectedSessionKey] =
        values?.[listingUuid]?.[selected_session_details_form_key]?.[
            selectedSessionKey
        ];

    deliveryDetails.delivery_address = values[delivery_address.field_name];
    deliveryDetails.delivery_pin_code = values[delivery_pin_code.field_name];
    deliveryDetails.delivery_state = values[delivery_state.field_name];
    deliveryDetails.delivery_city = values[delivery_city.field_name];

    const orderBumpsValues: IOrderBumpsFormValues =
        values?.[order_bumps_form_key] || {};

    let isMerchandiseOrderBumpSelected = false;
    Object.keys(orderBumpsValues).forEach(orderBumpUuid => {
        if (
            orderBumpsValues[orderBumpUuid].listingDetails.type ===
            LISTING_TYPES.MERCHANDISE
        ) {
            isMerchandiseOrderBumpSelected = true;
        }
        orderBumpsDetails[orderBumpUuid] = isMerchandiseOrderBumpSelected
            ? {
                  quantity:
                      orderBumpsValues[orderBumpUuid].selectedQuantity || 1,
              }
            : {
                  ...(!checkIsArrayEmpty(
                      orderBumpsValues[orderBumpUuid].listingDetails?.slots ||
                          []
                  ) && {
                      slot_uids: orderBumpsValues[
                          orderBumpUuid
                      ].listingDetails.slots?.map(item => item.uuid),
                  }),
              };
    });

    const multipleUsersHeadCount = values?.[multiple_user_count_key];

    const variantDetails = values?.[selected_merchandise_variant_form_key];

    return {
        usersDetails,
        sessionDetails,
        orderBumpsDetails,
        deliveryDetails,
        variantDetails,
        isMerchandiseOrderBumpSelected,
        multipleUsersHeadCount,
        merchandiseQuantity,
    };
};

export const useCheckoutForm = () => {
    const {
        hostName,
        listingUuid,
        listingType,
        title,
        thankyouUuid,
        communityUuid,
        dynamicLinkUuid,
        isCheckoutPagePreviewMode,
        availabilityChoice,
        allowPostBookingSlotSelection,
        enableMultipleUserCountFlow,
        isShiprocketEnabled,
        termsAndConditionsUrl,
        eInvoiceEnabled,
    } = useCheckoutBasicDetailsContext();

    const { initiateBooking } = useInitiateBooking();
    const { isPaymentProcessing } = useGetIsPaymentProcessing();
    const { setIsPaymentProcessing } = useSetIsPaymentProcessing();

    const dispatch = useDispatch();

    const priceDetails = useSelector(
        (mainState: IRepoV2ReduxState) =>
            mainState.checkoutPages.listingDetails[listingUuid]?.priceSection
                ?.data
    );

    const { totalAmount } = useGetPriceSummary();
    const { currency_name } = priceDetails || {};

    const bookingQuestions = useSelector(
        (mainState: IRepoV2ReduxState) =>
            mainState.checkoutPages.listingDetails[listingUuid]?.customQuestions
                ?.data?.booking_questions
    );

    const userDetails = useGetFormData<{ [userId: string]: IUserDetails }>({
        key: `${FORM_KEYS.user_details}`,
    });

    const allUserIds =
        useGetFormData<IMultipleUsersHelpers["allUserIds"]>({
            key: `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.all_user_ids}`,
        }) || [];

    // we need this to evaluate `user_type` for initiate payment payload
    const firstUserId = allUserIds.length
        ? allUserIds[0].userId
        : booking_initial_user_id;

    const paymentDetails =
        useGetFormData<IPaymentDetails>({
            key: `${FORM_KEYS.payment_details}`,
        }) || {};

    const {
        paymentMethod = "",
        paymentCalculationType = DEFAULT_PAYMENT_CARD_TYPE,
    } = paymentDetails;

    const partPaymentDetails =
        useSelector(
            (checkoutState: IRepoV2ReduxState) =>
                checkoutState.checkoutPages.listingDetails[listingUuid]
                    ?.partPaymentDetails?.data
        ) || {};

    const { installmentsList } = partPaymentDetails;

    // incase of checkout, we always pay the first installment
    const firstInstallment = installmentsList?.[0];

    const gstDetails = useGetFormData<IGSTDetails>({
        key: `${FORM_KEYS.gst_details}`,
    });

    const timezoneDetails = useGetFormData<ITimezoneDetails>({
        key: `${FORM_KEYS.timezone_details}`,
    });

    const allListingDetails =
        useGetFormData<IFormDataState["listingDetails"]>({
            key: `${FORM_KEYS.listing_details}`,
        }) || {};

    const showInternationalInputFields =
        priceDetails?.showInternationalInputFields || false;

    const { notify, toast_type_keys } = useToastMessage();

    const sessionDetails = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.sessionDetails
                ?.data
    );

    const { variants = {} } =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseVariantsDetails?.data
        ) || {};

    const shiprocketData =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseShiprocketDetails?.data
        ) || {};
    const isVariantMerchandise = Boolean(Object.keys(variants).length);

    const defaultValues = computeDefaultValuesFromRedux({
        userDetails,
        gstDetails,
        sessionDetails,
        timezoneDetails,
        paymentMethod,
        listingUuid,
        listingType,
        communityUuid,
        eInvoiceEnabled,
    });

    const handleSubmit = async ({ values }: { values: FieldValues }) => {
        if (isPaymentProcessing) return;

        if (isCheckoutPagePreviewMode) {
            return;
        }

        // Incase listing type is appointments, then we need to make
        // sure user has selected at least one slot to continue
        if (
            listingType === LISTING_TYPES.APPOINTMENTS &&
            availabilityChoice !== AVAILABILITY_CHOICE.NOT_DECIDED &&
            !allowPostBookingSlotSelection
        ) {
            const selectedSessionKey = session_selector_enum[listingType];
            const selectedSessionsValues =
                values?.[listingUuid]?.[selected_session_details_form_key]?.[
                    selectedSessionKey
                ];
            if (!selectedSessionsValues?.length) {
                notify("Please select a slot to continue!", {
                    type: toast_type_keys.error,
                });
                return;
            }
        }

        setIsPaymentProcessing(true);
        // @dev
        // for Multiusers implementation
        // fetching all the userIds list that are
        // available in UserDetails Redux state when multiple users are added
        // and gettting all form values corresponding to that User and other details as well
        const {
            usersDetails: usersDetailsPayload,
            sessionDetails: sessionDetailsPayload,
            orderBumpsDetails: orderBumpsDetailsPayload,
            deliveryDetails: deliveryDetailsPayload,
            variantDetails: variantDetailsPayload,
            isMerchandiseOrderBumpSelected,
            multipleUsersHeadCount,
            merchandiseQuantity,
        } = convertFormValuesToInitiateBookingPayload({
            values,
            userIds: Object.keys(userDetails),
            bookingQuestions,
            listingUuid,
            listingType,
            showInternationalInputFields,
        });

        if (checkIsListingClassesType({ listingType })) {
            if (!Object.keys(sessionDetailsPayload.batches || {}).length) {
                notify("Please select a batch to continue!", {
                    type: toast_type_keys.error,
                });
                setIsPaymentProcessing(false);
                return;
            }
        }

        // if listing is a variant merchandise, then variant must be selected by user
        if (
            listingType === LISTING_TYPES.MERCHANDISE &&
            isVariantMerchandise &&
            !variantDetailsPayload?.uuid
        ) {
            notify("Please selecte a variant to continue!", {
                type: toast_type_keys.error,
            });
            setIsPaymentProcessing(false);
            return;
        }

        // in case of merchandise, if user has not checked the shiprocket shipping charges, we need to check it
        if (
            listingType === LISTING_TYPES.MERCHANDISE &&
            isShiprocketEnabled &&
            !shiprocketData?.isVerified
        ) {
            if (shiprocketData?.error) {
                notify(shiprocketData.error, {
                    type: toast_type_keys.error,
                });
                setIsPaymentProcessing(false);
                return;
            } else {
                const userData = Object.values(usersDetailsPayload)?.[0] || {};
                const response = await checkMerchandiseShipping({
                    listingUuid,
                    currencyName: priceDetails.currency_name || "",
                    isInternationalEnabled:
                        priceDetails.showInternationalInputFields || false,
                    pinCode: userData.internationalDetails?.postal_code || "",
                    variantUuid: variantDetailsPayload.uuid || "",
                });

                // Incase merchandise shiprocket api gives error, we set the data
                // and the error message as such
                if (response?.isError) {
                    notify(merchandise_shiprocket_error, {
                        type: toast_type_keys.error,
                    });

                    dispatch({
                        type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
                        payload: {
                            listingUuid,
                            data: {
                                ...shiprocketData,
                                value: {},
                                isVerified: true,
                                error: merchandise_shiprocket_error,
                            },
                        },
                    });

                    setIsPaymentProcessing(false);
                    return;
                } else {
                    // else, we set the shipping charges and other data we get,
                    // this is used to calculate the final price of the listing
                    // as it includes the shipping charges
                    dispatch({
                        type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
                        payload: {
                            listingUuid,
                            data: {
                                ...shiprocketData,
                                value: {
                                    shippingCharge: response?.shipping_charge,
                                    shippingDays: response?.shipping_days,
                                },
                                isVerified: true,
                                error: "",
                            },
                        },
                    });
                }
            }
        }

        // incase terms and conditions exist, and user is yet to accept it, then we show error
        if (termsAndConditionsUrl && !values[agree_to_terms_and_conditions]) {
            notify("Please read and accept the Terms and Conditions", {
                type: toast_type_keys.error,
            });
            setIsPaymentProcessing(false);
            return;
        }

        const { showAddressInsideUserDetails, showAddressOutsideUserDetails } =
            getShowAddressFields({
                listingType,
                showInternationalInputFields,
                isMerchandiseOrderBumpSelected,
            });
        const addressDetailsPayloadHelper = {
            isSendAddressDetails:
                showAddressInsideUserDetails || showAddressOutsideUserDetails,
            showAddressInsideUserDetails,
        };

        const installmentDetails: {
            installmentUuid?: string;
            paymentType?: number;
        } = {};

        if (paymentCalculationType === PAYMENT_CALCULATION_TYPE.PART_PAYMENT) {
            installmentDetails.installmentUuid = firstInstallment?.uuid || "";
            // incase of part payment through checkout, we'll only be making initial
            // installment payment
            installmentDetails.paymentType =
                BOOKING_PAYMENT_TYPE.INITIAL_PART_PAYMENT;
        }

        const { names, emails, phoneNumbers } = Object.values(
            usersDetailsPayload
        ).reduce(
            (analyticsDataObj, customerDetails) => {
                const {
                    email: customerEmail,
                    name: customerName,
                    country_code: countryCode,
                    phone: customerPhone,
                } = customerDetails;

                analyticsDataObj.names.push(customerName as string);
                analyticsDataObj.emails.push(customerEmail as string);
                analyticsDataObj.phoneNumbers.push(
                    `${countryCode}${customerPhone}`
                );

                return analyticsDataObj;
            },
            {
                emails: [] as string[],
                names: [] as string[],
                phoneNumbers: [] as string[],
            }
        );

        /**
         * Anlaytics event trigger for Add Payment Info, triggered on payment info form submit
         */
        postAnalytics(ANALYTICS_EVENTS.ADD_PAYMENT_INFO, {
            sub_domain: hostName,
            event_source_url: window.location.href,
            value: totalAmount,
            currency: currency_name,
            items: [
                {
                    item_id: listingUuid,
                    item_name: title,
                },
            ],
            user_data: {
                em: emails,
                name: names,
                ph: phoneNumbers,
            },
        });

        try {
            initiateBooking({
                userDetails: usersDetailsPayload,
                sessionDetails: sessionDetailsPayload,
                deliveryDetails: deliveryDetailsPayload,
                gstDetails,
                variantDetails: variantDetailsPayload,
                listingUuid,
                listingType,
                orderBumpsDetails: orderBumpsDetailsPayload,
                addressDetailsPayloadHelper,
                allListingDetails,
                merchandiseQuantity,
                paymentMethod,
                firstUserId,
                installmentDetails,
                thankyouUuid,
                communityUuid,
                dynamicLinkUuid,
                ...(enableMultipleUserCountFlow && {
                    multipleUsersHeadCount,
                }),
                callPaymentGatewayOptionalCallbacks: {
                    samePageGatewayLoadCallback: () => {
                        // Re-enable button by setting the processing state as false.
                        // Only for the gateways that are loaded in the same screen as checkout page.
                        // So that if user closes the gateway modal UI, he/she can click on pay button again if needed.
                        //
                        // If we set the processing state as false for all gateways(even which cause redirecitons),
                        // the button becomes clickable for some time frame between redirection to gateway.
                        // So, what we do is keep the button disabled for gateways that redirect.
                        // And they find the button is enabled when they navigate back (while paying??? stupid 😒).
                        setIsPaymentProcessing(false);
                    },
                },
                callInitiateBookingOptionalCallbacks: {
                    stopBookingButtonProcessing: () => {
                        // Incase api will fail, the we will have to
                        // re enable the payment button, also stop the loader
                        setIsPaymentProcessing(false);
                    },
                },
            });
        } catch (err) {
            setIsPaymentProcessing(false);
        }
    };

    const handleError = () => {
        notify(checkout_form_error_strings.submit_all, {
            type: toast_type_keys.error,
        });
    };

    return {
        defaultValues,
        handleSubmit,
        handleError,
    };
};
